import { isNil } from "ramda";
import yn from "yn";
import { type FliptFlagKey } from "./constants";

export interface RouteDefinition {
  /** Permission required to access this route */
  requiredPermission?: string;
  /** Static feature flag (env var) - checked at build time */
  requiredFeatureFlag?: boolean;
  /** Dynamic Flipt flag key - checked at runtime per user */
  requiredFliptFlag?: FliptFlagKey;
}

export const checkHasRequiredFeatureFlagDefined = (item: RouteDefinition) =>
  "requiredFeatureFlag" in item;

export const checkHasRequiredFliptFlagDefined = (item: RouteDefinition) =>
  "requiredFliptFlag" in item;

/**
 * Filters route items based on permissions and feature flags.
 *
 * @param permissions - User's permissions array
 * @param items - Route items to filter
 * @param fliptFlags - Evaluated Flipt flag values (from useFliptFlags)
 */
export const useVisibleRoutes = <T extends RouteDefinition>(
  permissions: string[],
  items: T[],
  fliptFlags: Record<FliptFlagKey, boolean> = {} as Record<FliptFlagKey, boolean>
) => {
  return items.filter((item) => {
    const hasRequiredFeatureFlag = !checkHasRequiredFeatureFlagDefined(item)
      ? true
      : yn(item.requiredFeatureFlag);

    const hasRequiredFliptFlag = !checkHasRequiredFliptFlagDefined(item)
      ? true
      : (fliptFlags[item.requiredFliptFlag as FliptFlagKey] ?? false);

    // Check permission
    const hasRequiredPermission = isNil(item.requiredPermission)
      ? true
      : permissions.includes(item.requiredPermission);

    return hasRequiredFeatureFlag && hasRequiredFliptFlag && hasRequiredPermission;
  });
};
